import { useState } from "react";
import { PhoneCall, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const TIMES = ["Anytime", "Morning", "Afternoon", "Evening"];

export default function CallbackForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [preferredTime, setPreferredTime] = useState("Anytime");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !phone.trim()) {
      toast.error("Please add your name and phone number");
      return;
    }
    setBusy(true);
    try {
      await api.post("/callbacks", { name: name.trim(), phone: phone.trim(), preferred_time: preferredTime, message: message.trim() });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't send — please call us instead");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="cj-card p-8 text-center" data-testid="callback-success">
        <div className="mx-auto h-14 w-14 rounded-full bg-emerald-500/15 flex items-center justify-center">
          <Check className="h-7 w-7 text-emerald-400" />
        </div>
        <div className="font-display uppercase font-extrabold text-2xl text-slate-100 mt-4">You're on CJ's list</div>
        <p className="text-slate-400 mt-2">Thanks {name.split(" ")[0]} — CJ will call you back as soon as possible.</p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="cj-card p-6 sm:p-8" data-testid="callback-form">
      <div className="flex items-center gap-2">
        <PhoneCall className="h-4 w-4 text-[#ff6b00]" />
        <div className="font-mono text-[11px] uppercase tracking-[0.25em] text-[#ff6b00]">Request a callback</div>
      </div>
      <h3 className="font-display uppercase font-extrabold text-2xl text-slate-100 mt-2">Browsing after hours?</h3>
      <p className="text-slate-400 mt-1 text-sm">Leave your number and CJ will call you straight back to sort your detail.</p>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Your name</span>
          <input value={name} onChange={(e) => setName(e.target.value)} data-testid="callback-name" className="nb-input mt-1" placeholder="Full name" />
        </label>
        <label className="block">
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Phone</span>
          <input value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="callback-phone" className="nb-input mt-1" placeholder="04xx xxx xxx" />
        </label>
      </div>
      <label className="block mt-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Best time to call</span>
        <div className="mt-1 flex flex-wrap gap-2">
          {TIMES.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setPreferredTime(t)}
              data-testid={`callback-time-${t.toLowerCase()}`}
              className={`px-4 py-2 rounded-full text-sm border ${preferredTime === t ? "bg-[#ff6b00] text-[#0b0f17] border-[#ff6b00] font-bold" : "bg-[#0f1522] text-slate-300 border-white/10"}`}
            >
              {t}
            </button>
          ))}
        </div>
      </label>
      <label className="block mt-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">Anything we should know? (optional)</span>
        <textarea value={message} onChange={(e) => setMessage(e.target.value)} data-testid="callback-message" rows={2} className="nb-input mt-1" placeholder="Car, service you're after, suburb…" />
      </label>

      <button type="submit" disabled={busy} data-testid="callback-submit" className="cj-btn-primary mt-5 w-full px-6 py-3.5 rounded-full inline-flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PhoneCall className="h-4 w-4" />} Request my callback
      </button>
    </form>
  );
}
